package no.vareflow.app.domain

/**
 * Plain-Kotlin SHA-256, HMAC-SHA256, PBKDF2-HMAC-SHA256 and Base64, so local passwords can be
 * hashed and checked in the browser exactly as on the phone. The phone keeps using the Java
 * crypto provider (see LocalPasswords); CryptoTest checks both give identical results.
 */
object Crypto {
    private val K = intArrayOf(
        0x428a2f98, 0x71374491, -0x4a3f0431, -0x164a245b, 0x3956c25b, 0x59f111f1, -0x6dc07d5c, -0x54e3a12b,
        -0x27f85568, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, -0x7f214e02, -0x6423f959, -0x3e640e8c,
        -0x1b64963f, -0x1041b87a, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
        -0x67c1aeae, -0x57ce3993, -0x4ffcd838, -0x40a68039, -0x391ff40d, -0x2a586eb9, 0x06ca6351, 0x14292967,
        0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, -0x7e3d36d2, -0x6d8dd37b,
        -0x5d40175f, -0x57e599b5, -0x3db47490, -0x3893ae5d, -0x2e6d17e7, -0x2966f9dc, -0xbf1ca7b, 0x106aa070,
        0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
        0x748f82ee, 0x78a5636f, -0x7b3787ec, -0x7338fdf8, -0x6f410006, -0x5baf9315, -0x41065c09, -0x398e870e
    )
    private val H0 = intArrayOf(0x6a09e667, -0x4498517b, 0x3c6ef372, -0x5ab00ac6, 0x510e527f, -0x64fa9774, 0x1f83d9ab, 0x5be0cd19)

    /** One SHA-256 compression of the 16-word block [w] (first 16 entries) into [state]. */
    private fun compress(state: IntArray, w: IntArray) {
        for (t in 16 until 64) {
            val x = w[t - 15]
            val y = w[t - 2]
            val s0 = (x ushr 7 or (x shl 25)) xor (x ushr 18 or (x shl 14)) xor (x ushr 3)
            val s1 = (y ushr 17 or (y shl 15)) xor (y ushr 19 or (y shl 13)) xor (y ushr 10)
            w[t] = w[t - 16] + s0 + w[t - 7] + s1
        }
        var a = state[0]; var b = state[1]; var c = state[2]; var d = state[3]
        var e = state[4]; var f = state[5]; var g = state[6]; var h = state[7]
        for (t in 0 until 64) {
            val s1 = (e ushr 6 or (e shl 26)) xor (e ushr 11 or (e shl 21)) xor (e ushr 25 or (e shl 7))
            val ch = (e and f) xor (e.inv() and g)
            val t1 = h + s1 + ch + K[t] + w[t]
            val s0 = (a ushr 2 or (a shl 30)) xor (a ushr 13 or (a shl 19)) xor (a ushr 22 or (a shl 10))
            val maj = (a and b) xor (a and c) xor (b and c)
            val t2 = s0 + maj
            h = g; g = f; f = e; e = d + t1; d = c; c = b; b = a; a = t1 + t2
        }
        state[0] += a; state[1] += b; state[2] += c; state[3] += d
        state[4] += e; state[5] += f; state[6] += g; state[7] += h
    }

    private fun readWord(bytes: ByteArray, offset: Int): Int =
        (bytes[offset].toInt() and 0xff shl 24) or (bytes[offset + 1].toInt() and 0xff shl 16) or
            (bytes[offset + 2].toInt() and 0xff shl 8) or (bytes[offset + 3].toInt() and 0xff)

    private fun stateToBytes(state: IntArray): ByteArray = ByteArray(32).also { out ->
        for (i in 0 until 8) {
            out[i * 4] = (state[i] ushr 24).toByte()
            out[i * 4 + 1] = (state[i] ushr 16).toByte()
            out[i * 4 + 2] = (state[i] ushr 8).toByte()
            out[i * 4 + 3] = state[i].toByte()
        }
    }

    /** Hashes [data] starting from [initial] state, as if [prefixBytes] bytes were already hashed. */
    private fun sha256From(initial: IntArray, prefixBytes: Long, data: ByteArray): IntArray {
        val state = initial.copyOf()
        val totalBits = (prefixBytes + data.size) * 8
        val padded = ByteArray(((data.size + 9 + 63) / 64) * 64)
        data.copyInto(padded)
        padded[data.size] = 0x80.toByte()
        for (i in 0 until 8) padded[padded.size - 1 - i] = (totalBits ushr (8 * i)).toByte()
        val w = IntArray(64)
        var offset = 0
        while (offset < padded.size) {
            for (i in 0 until 16) w[i] = readWord(padded, offset + i * 4)
            compress(state, w)
            offset += 64
        }
        return state
    }

    fun sha256(data: ByteArray): ByteArray = stateToBytes(sha256From(H0, 0, data))

    private fun hmacKeyStates(key: ByteArray): Pair<IntArray, IntArray> {
        val block = ByteArray(64)
        (if (key.size > 64) sha256(key) else key).copyInto(block)
        val w = IntArray(64)
        val inner = H0.copyOf()
        for (i in 0 until 16) w[i] = readWord(block, i * 4) xor 0x36363636
        compress(inner, w)
        val outer = H0.copyOf()
        for (i in 0 until 16) w[i] = readWord(block, i * 4) xor 0x5c5c5c5c
        compress(outer, w)
        return inner to outer
    }

    fun hmacSha256(key: ByteArray, message: ByteArray): ByteArray {
        val (inner, outer) = hmacKeyStates(key)
        val innerHash = stateToBytes(sha256From(inner, 64, message))
        return stateToBytes(sha256From(outer, 64, innerHash))
    }

    /** PBKDF2-HMAC-SHA256 ([keyBytes] ≤ 32 is all the app needs, but longer keys work too). */
    fun pbkdf2Sha256(password: ByteArray, salt: ByteArray, iterations: Int, keyBytes: Int): ByteArray {
        require(iterations > 0 && keyBytes > 0)
        val (inner, outer) = hmacKeyStates(password)
        val result = ByteArray(keyBytes)
        // Fast path for the iterated part: each round hashes a single 32-byte value, which after
        // the precomputed key block always fits one padded block of fixed shape.
        val w = IntArray(64)
        val st = IntArray(8)
        fun hmacWords(words: IntArray): IntArray {
            // inner: H(inner ‖ words ‖ padding), total length 64 + 32 bytes
            for (i in 0 until 8) w[i] = words[i]
            w[8] = 0x80000000.toInt(); for (i in 9 until 15) w[i] = 0; w[15] = (64 + 32) * 8
            inner.copyInto(st); compress(st, w)
            for (i in 0 until 8) w[i] = st[i]
            w[8] = 0x80000000.toInt(); for (i in 9 until 15) w[i] = 0; w[15] = (64 + 32) * 8
            val out = outer.copyOf(); compress(out, w)
            return out
        }
        var blockIndex = 1
        var written = 0
        while (written < keyBytes) {
            val saltBlock = salt + byteArrayOf((blockIndex ushr 24).toByte(), (blockIndex ushr 16).toByte(), (blockIndex ushr 8).toByte(), blockIndex.toByte())
            val first = sha256From(outer, 64, stateToBytes(sha256From(inner, 64, saltBlock)))
            var u = first
            val acc = first.copyOf()
            for (round in 1 until iterations) {
                u = hmacWords(u)
                for (i in 0 until 8) acc[i] = acc[i] xor u[i]
            }
            val bytes = stateToBytes(acc)
            val take = minOf(32, keyBytes - written)
            bytes.copyInto(result, written, 0, take)
            written += take
            blockIndex++
        }
        return result
    }

    fun constantTimeEquals(a: ByteArray, b: ByteArray): Boolean {
        if (a.size != b.size) return false
        var diff = 0
        for (i in a.indices) diff = diff or (a[i].toInt() xor b[i].toInt())
        return diff == 0
    }

    private const val ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/"

    /** Standard Base64 alphabet, no padding (same text as the phone stores). */
    fun base64Encode(bytes: ByteArray): String {
        val out = StringBuilder((bytes.size * 4 + 2) / 3)
        var i = 0
        while (i + 2 < bytes.size) {
            val n = (bytes[i].toInt() and 0xff shl 16) or (bytes[i + 1].toInt() and 0xff shl 8) or (bytes[i + 2].toInt() and 0xff)
            out.append(ALPHABET[n ushr 18 and 63]).append(ALPHABET[n ushr 12 and 63]).append(ALPHABET[n ushr 6 and 63]).append(ALPHABET[n and 63])
            i += 3
        }
        val rest = bytes.size - i
        if (rest == 1) {
            val n = bytes[i].toInt() and 0xff shl 16
            out.append(ALPHABET[n ushr 18 and 63]).append(ALPHABET[n ushr 12 and 63])
        } else if (rest == 2) {
            val n = (bytes[i].toInt() and 0xff shl 16) or (bytes[i + 1].toInt() and 0xff shl 8)
            out.append(ALPHABET[n ushr 18 and 63]).append(ALPHABET[n ushr 12 and 63]).append(ALPHABET[n ushr 6 and 63])
        }
        return out.toString()
    }

    /** Decodes standard Base64 with or without padding; null for anything invalid. */
    fun base64Decode(text: String): ByteArray? {
        val clean = text.trimEnd('=')
        if (text.length - clean.length > 2 || clean.length % 4 == 1) return null
        val out = ByteArray(clean.length * 3 / 4)
        var buffer = 0
        var bits = 0
        var index = 0
        for (c in clean) {
            val v = ALPHABET.indexOf(c)
            if (v < 0) return null
            buffer = (buffer shl 6) or v
            bits += 6
            if (bits >= 8) {
                bits -= 8
                out[index++] = (buffer ushr bits).toByte()
            }
        }
        return out
    }
}

/** Cryptographically secure random bytes from the platform. */
expect fun secureRandomBytes(count: Int): ByteArray

/** PBKDF2-HMAC-SHA256 of [password] (UTF-8), 32 bytes. Platform crypto where available. */
internal expect fun derivePasswordKey(password: String, salt: ByteArray, iterations: Int): ByteArray
